import React, { FC, Suspense } from 'react'
import { Loading, TitleController } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import {
  ResidentAppAsideSection,
  ResidentAppAsideText,
  ResidentAppBackLink,
  ResidentAppCard,
  ResidentAppView,
} from '@pandiafi/components'

import { DefectNoticeForm } from './Form'
import { DefectNoticeSuccess } from './Success'
import { DataLoader } from './DefectNoticeDataLoader'
import { ErrorHandler } from './DefectNoticeErrorHandler'

import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { ContactInfoErrorHandler } from 'views/ContactInfo/ContactInfoErrorHandler'
import { ContactInfoDataLoader } from 'views/ContactInfo/ContactInfoDataLoader'
import { MaintenanceContacts } from 'views/ContactInfo/MaintenanceContacts'
import DefectNotices from 'components/DefectNotices'

export type DefectNoticeViewState = 'form' | 'preview' | 'success'

export const DefectNoticeView: FC = () => {
  const [view, setView] = React.useState<DefectNoticeViewState>('form')
  const { t } = useTranslation(['common', 'defectNotice', 'titles'])
  const { frontPagePath } = useContractPaths()

  return (
    <TitleController title={t('titles:defectNotice')}>
      <ResidentAppView
        title={t('defectNotice:headings.defectNotice')}
        backLink={
          <ResidentAppBackLink href={frontPagePath}>
            {t('common:views.home')}
          </ResidentAppBackLink>
        }
        aside={
          <>
            <DefectNotices />
            <ResidentAppAsideSection>
              <ResidentAppAsideText>
                {t('defectNotice:texts.instructions')}
              </ResidentAppAsideText>
            </ResidentAppAsideSection>
            <Suspense fallback={<Loading variant="details" />}>
              <ContactInfoErrorHandler>
                <ContactInfoDataLoader>
                  {({ contacts }) => (
                    <MaintenanceContacts contacts={contacts} />
                  )}
                </ContactInfoDataLoader>
              </ContactInfoErrorHandler>
            </Suspense>
          </>
        }
      >
        <ResidentAppCard>
          <ErrorHandler>
            <DataLoader>
              {(data) =>
                view === 'success' ? (
                  <DefectNoticeSuccess editMode={false} />
                ) : (
                  <DefectNoticeForm {...data} view={view} setView={setView} />
                )
              }
            </DataLoader>
          </ErrorHandler>
        </ResidentAppCard>
      </ResidentAppView>
    </TitleController>
  )
}
